import React, { createContext, useContext, ReactNode } from 'react';

import { useDropdownManager, DropdownType } from 'src/hooks/useDropdownManager';

interface DropdownContextType {
  activeDropdown: DropdownType;
  isDropdownOpen: (type: DropdownType) => boolean;
  openDropdown: (type: DropdownType) => void;
  closeDropdown: () => void;
  toggleDropdown: (type: DropdownType) => void;
  registerDropdownRef: (type: DropdownType, ref: HTMLElement | null) => void;
}

const DropdownContext = createContext<DropdownContextType | undefined>(undefined);

interface DropdownProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages dropdown state across the application.
 * Ensures only one dropdown is open at a time and handles outside click detection.
 */
export const DropdownProvider: React.FunctionComponent<DropdownProviderProps> = ({ children }) => {
  const dropdownManager = useDropdownManager();

  return <DropdownContext.Provider value={dropdownManager}>{children}</DropdownContext.Provider>;
};

/**
 * Hook to access dropdown context. Must be used within a DropdownProvider.
 */
export const useDropdownContext = (): DropdownContextType => {
  const context = useContext(DropdownContext);
  if (context === undefined) {
    throw new Error('useDropdownContext must be used within a DropdownProvider');
  }
  return context;
};
